import React, { useContext } from "react";
import { v4 as uuidV4 } from "uuid";
import useLocalStorage from "../hooks/useLocalStorage";
import { filterThisWeek } from "../utils";
import {
  startOfWeek,
  endOfWeek,
  isWithinInterval,
  eachWeekOfInterval,
  subWeeks,
  format,
} from "date-fns";

const BudgetsContext = React.createContext();

export const UNCATEGORIZED_BUDGET_ID = "Uncategorized";

const calculateWeeklyHistory = (expenses) => {
  // Get the date range for the last 8 weeks (or however many you want)
  const today = new Date();
  const eightWeeksAgo = subWeeks(today, 8);

  // Get all weeks between eightWeeksAgo and today
  const weeks = eachWeekOfInterval(
    { start: eightWeeksAgo, end: today },
    { weekStartsOn: 1 } // Monday
  );

  // For each week, calculate total expenses
  return weeks
    .map((weekStart) => {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

      // Filter expenses that fall within this week
      const weeklyExpenses = expenses.filter((expense) => {
        const expenseDate = new Date(expense.date);
        return isWithinInterval(expenseDate, {
          start: weekStart,
          end: weekEnd,
        });
      });

      // Calculate total for the week
      const weeklyTotal = weeklyExpenses.reduce(
        (sum, expense) => sum + expense.amount,
        0
      );

      //calculate how many aligned purchases
      const weeklyAligned = weeklyExpenses.reduce(
        (sum, expense) => sum + expense.aligned,
        0
      );

      // Format the week range for display
      const weekRange = `${format(weekStart, "MMM d")} - ${format(
        weekEnd,
        "MMM d, yyyy"
      )}`;

      return {
        id: weekStart.toISOString(),
        amount: weeklyTotal,
        max: 1000, // You might want to make this configurable per week or use a budget
        date: weekRange,
        aligned: weeklyAligned,
        expenseCount: weeklyExpenses.length,
      };
    })
    .reverse(); // Show most recent weeks first
};

export function useBudgets() {
  return useContext(BudgetsContext);
}

export const BudgetsProvider = ({ children }) => {
  const [budgets, setBudgets] = useLocalStorage("budgets", []);
  const [expenses, setExpenses] = useLocalStorage("expenses", []);
  //const [history, setHistory] = useLocalStorage("history", []);

  const history = calculateWeeklyHistory(expenses);

  function getBudgetExpenses(budgetId) {
    const filteredExpenses = expenses.filter(
      (expense) => expense.budgetId === budgetId
    );
    return filterThisWeek(filteredExpenses);
  }
  function addExpense({ description, amount, date, aligned, budgetId }) {
    setExpenses((prevExpenses) => {
      return [
        ...prevExpenses,
        { id: uuidV4(), description, amount, date, aligned, budgetId },
      ];
    });
  }
  function addBudget({ name, max }) {
    setBudgets((prevBudgets) => {
      if (prevBudgets.find((budget) => budget.name === name)) {
        return prevBudgets;
      }
      return [...prevBudgets, { id: uuidV4(), name, max }];
    });
  }
  function deleteBudget({ id }) {
    setExpenses((prevExpenses) => {
      return prevExpenses.map((expense) => {
        if (expense.budgetId !== id) return expense;
        return { ...expense, budgetId: UNCATEGORIZED_BUDGET_ID };
      });
    });
    setBudgets((prevBudgets) => {
      return prevBudgets.filter((budget) => budget.id !== id);
    });
  }
  function deleteExpense({ id }) {
    setExpenses((prevExpenses) => {
      return prevExpenses.filter((expense) => expense.id !== id);
    });
  }
  function getHistory() {
    return expenses;
  }
  function addHistory() {
    const amount = expenses.reduce(
      (total, expense) => total + expense.amount,
      0
    );
    const max = budgets.reduce((total, budget) => total + budget.max, 0);
    const date = getDate();
    //setHistory((prevHistory) => {
    //return [...prevHistory, { id: uuidV4(), amount, max, date }];
    //});
    return;
  }
  function getDate() {
    const today = new Date();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    const date = today.getDate();
    return `${month}/${date}/${year}`;
  }
  function clearHistory() {
    //localStorage.removeItem("budgets")
    localStorage.removeItem("expenses");
  }
  return (
    <BudgetsContext.Provider
      value={{
        budgets,
        expenses,
        getBudgetExpenses,
        addExpense,
        addBudget,
        deleteBudget,
        deleteExpense,
        addHistory,
        history,
        clearHistory,
        getHistory,
      }}
    >
      {children}
    </BudgetsContext.Provider>
  );
};
